import { registerStyles } from "../utils/registerStyles";
import React, { useEffect, useRef, useState } from "react";
import { FaBell, FaTimes, FaPlus, FaMinus, FaInfoCircle } from "react-icons/fa";
import { notificationSettingsError } from "../utils/scheduleNotifications";
import notificationInfoImage from "../images/info.PNG";

const hourFields = { overtime: "overtimeStartHours", overtimeLimit: "overtimeLimitHours", dailyRest: "dailyRestHours" };
const validHours = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

export default function NotificationsPopup({ onClose, settings, messages, onSave, onClearAll, onClearMessage, error, currentDate }) {
  const [draft, setDraft] = useState(() => ({
    ...settings,
    employment: settings.enabled && settings.employment,
    overtime: settings.enabled && settings.overtime,
    overtimeLimit: settings.enabled && settings.overtimeLimit,
    dailyRest: settings.enabled && settings.dailyRest,
    dailyRestHours: settings.dailyRestHours ?? "",
  }));
  const [showInfo, setShowInfo] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [validation, setValidation] = useState("");
  const change = (key, value) => {
    const next = { ...draft, [key]: value };
    for (const [rule, field] of Object.entries(hourFields)) {
      if (!validHours(next[field])) next[rule] = false;
    }
    next.enabled = Boolean(next.employment || next.overtime || next.overtimeLimit || next.dailyRest);
    setDraft(next);
    setValidation(notificationSettingsError(next));
    onSave(next);
  };
  const toggle = (key) => {
    const field = hourFields[key];
    if (!draft[key] && field) {
      setExpanded((previous) => ({ ...previous, [key]: true }));
      if (!validHours(draft[field])) {
        setValidation("Fyll i ett antal timmar större än 0 och slå sedan på notisen.");
        return;
      }
    }
    change(key, !draft[key]);
  };
  const closeRef = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    const handleKey = (event) => { if (event.key === "Escape") closeRef.current?.click(); };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return (
    <aside className="schedule-notifications" aria-labelledby="schedule-notifications-title">
      <header>
        <div><div className="schedule-notifications-title-row">
          <h2 id="schedule-notifications-title"><FaBell /> Notifikationer</h2>
                <button type="button" className="schedule-notifications-info-button"
                  aria-label="Information om arbetstid och övertid" aria-expanded={showInfo}
                  aria-controls="schedule-notifications-info-image" title="Information om arbetstid och övertid"
                  onClick={() => setShowInfo((visible) => !visible)}>
                  <FaInfoCircle aria-hidden="true" />
                </button>
        </div><p>Meddelanden till dig som administratör</p></div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Stäng notifikationer"><FaTimes /></button>
      </header>
      <div className="schedule-notifications-content">
        <form onSubmit={(event) => event.preventDefault()}>
          <fieldset className="schedule-notifications-fields" aria-label="Notisinställningar">
            {showInfo && <div id="schedule-notifications-info-image" className="schedule-notifications-info-image">
              <img src={notificationInfoImage} alt="Diagram över heltid och deltid: ordinarie arbetstid, arbetstidsmått, mertid och övertid, med exempelgränser på 115, 165 och 215 timmar." />
              <button type="button" onClick={() => setShowInfo(false)}>Stäng information</button>
            </div>}
            {[
              ["employment", "När arbetstidsmåttets procent överskrids", "Meddelar när personen går över till mertid."],
              ["overtime", "När personen går över till övertid"],
              ["overtimeLimit", "När övertidsgränsen överskrids", "Meddelar när den totala schemalagda tiden överstiger gränsen."],
              ["dailyRest", "Dygnsvila mellan passen", "Ange själv minsta antal timmar mellan passen. En notis visas när tiden mellan passen är kortare."],
            ].map(([key, label, description]) => (
              <div key={key}>
                <div className="schedule-notifications-setting">
                  <span>{label}{description && <small>{description}</small>}</span>
                  <div className="schedule-notifications-controls">
                    <button className="schedule-notifications-switch" type="button" role="switch"
                      aria-label={label} aria-checked={Boolean(draft[key])}
                      onClick={() => toggle(key)}><span /></button>
                    {key !== "employment" && <button type="button" className="schedule-notifications-expand"
                      aria-label={`${expanded[key] ? "Dölj" : "Visa"} ${key === "overtime" ? "övertidsstart" : key === "dailyRest" ? "dygnsvila" : "övertidsgräns"}`}
                      aria-expanded={Boolean(expanded[key])} aria-controls={`notification-hours-${key}`}
                      onClick={() => setExpanded((previous) => ({ ...previous, [key]: !previous[key] }))}>
                      {expanded[key] ? <FaMinus aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
                    </button>}
                  </div>
                </div>
                {key === "overtime" && expanded[key] && <label className="schedule-notifications-number" id={`notification-hours-${key}`}>
                  Övertid börjar efter (timmar per månad)
                  <input type="number" min="0.1" step="0.1" placeholder="Till exempel 160"
                    value={draft.overtimeStartHours} onChange={(e) => change("overtimeStartHours", e.target.value)} />
                </label>}
                {key === "dailyRest" && expanded[key] && <label className="schedule-notifications-number" id={`notification-hours-${key}`}>
                  Minsta antal timmar mellan passen
                  <input type="number" min="0.1" step="0.1" placeholder="Ange antal timmar"
                    value={draft.dailyRestHours} onChange={(e) => change("dailyRestHours", e.target.value)} />
                </label>}
                {key === "overtimeLimit" && expanded[key] && <label className="schedule-notifications-number" id={`notification-hours-${key}`}>
                  Övertidsgräns (totalt antal timmar per månad)
                  <input type="number" min="0.1" step="0.1" placeholder="Till exempel 215"
                    value={draft.overtimeLimitHours} onChange={(e) => change("overtimeLimitHours", e.target.value)} />
                </label>}
              </div>
            ))}
          </fieldset>
          {(validation || error) && <p role="alert" className="schedule-notifications-error">{error || validation}</p>}
        </form>
        <div className="schedule-notifications-messages-header">
        <h3 id="schedule-notifications-messages-title">Meddelanden · {currentDate.toLocaleDateString("sv-SE", { month: "long", year: "numeric" })}</h3>
          <button type="button" onClick={onClearAll} disabled={!messages.length}>Rensa alla</button>
        </div>
        <div className="schedule-notifications-messages" role="region" aria-labelledby="schedule-notifications-messages-title" tabIndex={0} aria-live="polite">
          {messages.length === 0 ? <p className="schedule-notifications-empty">{settings.enabled ? "Inga notiser för de valda gränserna." : "Notiser är avstängda."}</p>
            : messages.map((message) => <article className="schedule-notifications-message" key={message.id}>
              <button type="button" className="schedule-notifications-message-clear"
                onClick={() => onClearMessage(message.id)} aria-label={`Rensa notis: ${message.name} – ${message.title}`} title="Rensa notis">
                <FaTimes aria-hidden="true" />
              </button>
              <strong>{message.name}</strong><p><b>{message.title}</b></p><p>{message.text}</p>
            </article>)}
        </div>
      </div>
    </aside>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("NotificationsPopup", `
.schedule-notifications { position: fixed; top: 0; right: 0; width: 400px; max-width: 100vw; height: 100dvh; z-index: 9999; display: flex; flex-direction: column; background: white; color: #0f172a; box-shadow: -8px 0 30px #0f172a29; }
.schedule-notifications header { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px; background: #f5f3ff; border-bottom: 1px solid #ddd6fe; }
.schedule-notifications h2 { display: flex; align-items: center; gap: 9px; margin: 0; font-size: 18px; }
.schedule-notifications h2 svg { color: #7c3aed; }
.schedule-notifications header p { margin: 6px 0 0; color: #64748b; font-size: 12px; }
.schedule-notifications header button { width: 32px; height: 32px; border: 0; border-radius: 8px; background: white; color: #475569; cursor: pointer; flex-shrink: 0; }
.schedule-notifications-content { padding: 6px 20px 20px; flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
.schedule-notifications-setting { display: flex; align-items: center; justify-content: space-between; gap: 14px; font-size: 14px; font-weight: 600; line-height: 1.5; cursor: pointer; }
.schedule-notifications-setting input { margin-top: 4px; accent-color: #7c3aed; }
.schedule-notifications-help, .schedule-notifications-empty { color: #64748b; font-size: 13px; line-height: 1.6; }
.schedule-notifications h3 { font-size: 14px; margin: 28px 0 14px; }
.schedule-notifications-message { padding: 16px; margin-bottom: 12px; background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #f59e0b; border-radius: 10px; font-size: 14px; }
.schedule-notifications-message p { line-height: 1.5; margin: 8px 0; }
.schedule-notifications-message small { color: #92400e; }
.schedule-notifications-switch { width: 58px; height: 32px; border: 0; border-radius: 999px; padding: 3px; background: #cbd5e1; cursor: pointer; flex-shrink: 0; transition: background .2s; }
.schedule-notifications-switch span { display: block; width: 26px; height: 26px; border-radius: 50%; background: white; box-shadow: 0 1px 3px #0002; transition: transform .2s; }
.schedule-notifications-switch[aria-checked="true"] { background: #31c455; }
.schedule-notifications-switch[aria-checked="true"] span { transform: translateX(26px); }
.schedule-notifications-fields { border: 0; margin: 0; padding: 0; min-width: 0; }
.schedule-notifications-fields .schedule-notifications-setting { padding: 14px 0; border-bottom: 1px solid #e2e8f0; }
.schedule-notifications-setting small { display: block; font-weight: 400; color: #64748b; margin-top: 4px; }
.schedule-notifications-number { display: flex; flex-direction: column; gap: 8px; font-size: 13px; font-weight: 600; margin-top: 20px; }
.schedule-notifications-number input { padding: 12px; border: 1px solid #cbd5e1; border-radius: 8px; width: 100%; box-sizing: border-box; font: inherit; }
.schedule-notifications-number input:disabled { background: #f1f5f9; }
.schedule-notifications-save { width: 100%; border: 0; border-radius: 8px; background: #7c3aed; color: white; padding: 12px; font: inherit; font-weight: 600; cursor: pointer; margin-top: 14px; }
.schedule-notifications-error { color: #b91c1c; font-size: 13px; }
.schedule-notifications-success { color: #15803d; font-size: 13px; }
.schedule-notifications-badge { position: absolute; right: -5px; top: -5px; min-width: 18px; height: 18px; padding: 0 3px; box-sizing: border-box; display: grid; place-items: center; border-radius: 999px; border: 2px solid white; background: #ef4444; color: white; font-size: 10px; font-weight: 700; pointer-events: none; }
.schedule-notifications-toast { position: fixed; top: 20px; right: 20px; z-index: 10000; width: 380px; max-width: calc(100vw - 40px); box-sizing: border-box; display: flex; align-items: flex-start; gap: 12px; padding: 16px; background: white; border: 1px solid #ddd6fe; border-left: 4px solid #8b5cf6; border-radius: 12px; box-shadow: 0 8px 32px #0f172a26; color: #7c3aed; }
.schedule-notifications-toast-open { flex: 1; border: 0; background: none; padding: 0; color: #0f172a; text-align: left; cursor: pointer; font: inherit; font-size: 13px; line-height: 1.5; }
.schedule-notifications-toast-open span, .schedule-notifications-toast-open small { display: block; margin-top: 5px; color: #64748b; }
.schedule-notifications-toast-close { border: 0; background: none; padding: 0; color: #64748b; cursor: pointer; }
.schedule-notifications button:focus-visible, .schedule-notifications input:focus-visible { outline: 2px solid #7c3aed; outline-offset: 3px; }

.schedule-notifications-controls { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.schedule-notifications-expand { display: grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 1px solid #ddd6fe; border-radius: 7px; background: #f5f3ff; color: #7c3aed; cursor: pointer; font-size: 11px; }
.schedule-notifications-expand:hover { background: #ede9fe; }

.schedule-notifications-title-row { display: flex; align-items: center; gap: 8px; }
.schedule-notifications header .schedule-notifications-info-button { display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 50%; background: #f5f3ff; color: #7c3aed; cursor: pointer; font-size: 16px; }
.schedule-notifications-info-image { margin: 0 0 16px; padding: 10px; border: 1px solid #ddd6fe; border-radius: 10px; background: white; }
.schedule-notifications-info-image img { display: block; width: 100%; height: auto; }
.schedule-notifications-info-image button { display: block; margin: 10px auto 0; padding: 6px 10px; border: 0; border-radius: 6px; background: #f5f3ff; color: #7c3aed; cursor: pointer; font: inherit; font-size: 12px; }

.schedule-notifications > header { flex-shrink: 0; }
.schedule-notifications-content > form { flex: 0 1 auto; min-height: 0; overflow-y: auto; }
.schedule-notifications-messages-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 24px 0 14px; flex-shrink: 0; }
.schedule-notifications-messages-header h3 { margin: 0; }
.schedule-notifications-messages-header button { flex-shrink: 0; border: 0; border-radius: 6px; padding: 8px 10px; background: #f5f3ff; color: #7c3aed; font: inherit; font-size: 12px; cursor: pointer; }
.schedule-notifications-messages-header button:disabled { opacity: .5; cursor: default; }
.schedule-notifications-messages { flex: 1 0 100px; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.schedule-notifications-messages:focus-visible { outline: 2px solid #7c3aed; outline-offset: 2px; }

.schedule-notifications-message { position: relative; padding-right: 40px; }
.schedule-notifications-message-clear { position: absolute; top: 8px; right: 8px; display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #92400e; cursor: pointer; font-size: 12px; }
.schedule-notifications-message-clear:hover { background: #fef3c7; }
`);
